import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Scroll from "react-scroll";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilterCircleXmark,
  faFilter,
  faArrowLeft,
  faUsers,
  faTachometer,
  faEye,
  faPencil,
  faTrashCan,
  faPlus,
  faTable,
  faArrowRight,
  faRefresh,
  faArrowUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import { getSessionListAPI, deleteSessionAPI } from "../../API/Session";
import { useRecoilState } from "recoil";

import { getReportDetailAPI } from "../../API/Report";
import FormErrorBox from "../Reusable/FormErrorBox";
import FormSelectField from "../Reusable/FormSelectField";
import PageLoadingContent from "../Reusable/PageLoadingContent";
import {
  topAlertMessageState,
  topAlertStatusState,
  userSessionsShowFilterState,
  userSessionsSessionTypeState,
  userSessionsSortedByState,
  userDetailState,
} from "../../AppState";
import UserSessionTemplateListDesktop from "./DetailForSessionListDesktop";
import UserSessionTemplateListMobile from "./DetailForSessionListMobile";
import {
  SESSION_TEMPLATE_SORT_OPTIONS,
  SESSION_TEMPLATE_SESSION_TYPE_WITH_EMPTY_OPTIONS,
} from "../../Constants/FieldOptions";
import toast from "react-hot-toast";

function UserDetailForSessionList() {
  ////
  //// URL Parameters.
  ////

  const { uid } = useParams();

  ////
  //// Global state.
  ////

  const [topAlertMessage, setTopAlertMessage] =
    useRecoilState(topAlertMessageState);
  const [topAlertStatus, setTopAlertStatus] =
    useRecoilState(topAlertStatusState);
  const [showFilter, setShowFilter] = useRecoilState(
    userSessionsShowFilterState
  );
  const [sessionType, setSessionType] = useRecoilState(
    userSessionsSessionTypeState
  ); // Filtering
  const [sortByValue, setSortByValue] = useRecoilState(
    userSessionsSortedByState
  ); // Sorting
  const [user] = useRecoilState(userDetailState);

  ////
  //// Component states.
  ////

  const [errors, setErrors] = useState({});
  const [selectedSessionForDeletion, setSelectedSessionForDeletion] =
    useState("");
  const [isFetching, setFetching] = useState(false);
  const [listData, setListData] = useState(""); // List Data + Pagination
  const [pageSize, setPageSize] = useState(100); // Pagination
  const [previousCursors, setPreviousCursors] = useState([]); // Pagination
  const [nextCursor, setNextCursor] = useState(""); // Pagination
  const [currentCursor, setCurrentCursor] = useState(""); // Pagination

  ////
  //// API.
  ////

  function onUserDetailForSessionListSuccess(response) {
    console.log("onUserDetailForSessionListSuccess: Starting...");
    setListData(response);
    if (response.hasNextPage) {
      setNextCursor(response.nextCursor); // For pagination purposes.
    }
  }

  function onUserDetailForSessionListError(apiErr) {
    console.log("onUserDetailForSessionListError: Starting...");
    setErrors(apiErr);

    // The following code will cause the screen to scroll to the top of
    // the page. Please see ``react-scroll`` for more information:
    // https://github.com/fisshy/react-scroll
    var scroll = Scroll.animateScroll;
    scroll.scrollToTop();
  }

  function onUserDetailForSessionListDone() {
    console.log("onUserDetailForSessionListDone: Starting...");
    setFetching(false);
  }

  function onSessionDeleteSuccess(response) {
    console.log("onSessionDeleteSuccess: Starting..."); // For debugging purposes only.

    // Update notification.
    setTopAlertStatus("success");
    setTopAlertMessage("Session deleted");
    setTimeout(() => {
      console.log(
        "onDeleteConfirmButtonClick: topAlertMessage, topAlertStatus:",
        topAlertMessage,
        topAlertStatus
      );
      setTopAlertMessage("");
    }, 2000);

    // Fetch again an updated list.
    defaultFetchList();
  }

  function onSessionDeleteError(apiErr) {
    console.log("onSessionDeleteError: Starting..."); // For debugging purposes only.
    setErrors(apiErr);

    // Update notification.
    setTopAlertStatus("danger");
    setTopAlertMessage("Failed deleting");
    setTimeout(() => {
      console.log(
        "onSessionDeleteError: topAlertMessage, topAlertStatus:",
        topAlertMessage,
        topAlertStatus
      );
      setTopAlertMessage("");
    }, 2000);

    // The following code will cause the screen to scroll to the top of
    // the page. Please see ``react-scroll`` for more information:
    // https://github.com/fisshy/react-scroll
    var scroll = Scroll.animateScroll;
    scroll.scrollToTop();
  }

  function onSessionDeleteDone() {
    console.log("onSessionDeleteDone: Starting...");
    setFetching(false);
  }

  ////
  //// Event handling.
  ////

  const onClearFilterClick = (e) => {
    setShowFilter(false);
    setSessionType("");
    setSortByValue("completed_time,ASC");
  };

  // Convienience function.
  const defaultFetchList = () => {
    fetchList(currentCursor, pageSize, sortByValue, uid, sessionType);
  };

  const fetchList = (cur, limit, sbv, uid, st) => {
    setFetching(true);
    setErrors({});

    let params = new Map();
    params.set("page_size", limit); // Pagination

    // DEVELOPERS NOTE: Our `sortByValue` is string with the sort field
    // and sort order combined with a comma seperation. Therefore we
    // need to split as follows.
    if (sbv !== undefined && sbv !== null && sbv !== "") {
      const sortArray = sbv.split(",");
      params.set("sort_field", sortArray[0]); // Sort (1 of 2)
      params.set("sort_order", sortArray[1]); // Sort (2 of 2)
    }

    if (cur !== "") {
      params.set("cursor", cur); // Pagination
    }

    if (uid !== "") {
      // Pagination
      params.set("user_id", uid);
    }

    if (st !== "") {
      params.set("session_type", st); // Pagination
    }

    getSessionListAPI(
      params,
      onUserDetailForSessionListSuccess,
      onUserDetailForSessionListError,
      onUserDetailForSessionListDone
    );
  };

  const onNextClicked = (e) => {
    console.log("onNextClicked");
    let arr = [...previousCursors];
    arr.push(currentCursor);
    setPreviousCursors(arr);
    setCurrentCursor(nextCursor);
  };

  const onPreviousClicked = (e) => {
    console.log("onPreviousClicked");
    let arr = [...previousCursors];
    const previousCursor = arr.pop();
    setPreviousCursors(arr);
    setCurrentCursor(previousCursor);
  };

  const onSelectSessionForDeletion = (e, session) => {
    console.log("onSelectSessionForDeletion", session);
    setSelectedSessionForDeletion(session);
  };

  const onDeselectSessionForDeletion = (e) => {
    console.log("onDeselectSessionForDeletion");
    setSelectedSessionForDeletion("");
  };

  const onDeleteConfirmButtonClick = (e) => {
    console.log("onDeleteConfirmButtonClick"); // For debugging purposes only.

    deleteSessionAPI(
      selectedSessionForDeletion.id,
      onSessionDeleteSuccess,
      onSessionDeleteError,
      onSessionDeleteDone
    );
    setSelectedSessionForDeletion("");
  };

 

  const onReportClick = async (userID) => {
    try {
      setFetching(true);
      const response = await getReportDetailAPI(userID);

      if (response && response.data) {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const fileURL = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = fileURL;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.success("Report Created Successfully");
      } else {
        toast.error("Failed to generate report");
      }
    } catch (error) {
      toast.error("Error generating report");
    } finally {
      setFetching(false);
    }
  };
  ////
  //// Misc.
  ////

  useEffect(() => {
    let mounted = true;

    if (mounted) {
      window.scrollTo(0, 0); // Start the page at the top of the page.
      fetchList(currentCursor, pageSize, sortByValue, uid, sessionType);
    }

    return () => {
      mounted = false;
    };
  }, [currentCursor, pageSize, sortByValue, uid, sessionType]);

  ////
  //// Component rendering.
  ////

  return (
    <>
      <div class="container">
        <section class="section">
          {/* Desktop Breadcrumbs */}
          <nav class="breadcrumb is-hidden-touch" aria-label="breadcrumbs">
            <ul>
              <li class="">
                <Link to="/dashboard">
                  <FontAwesomeIcon className="fas" icon={faTachometer} />
                  &nbsp;Dashboard
                </Link>
              </li>
              <li class="">
                <Link to="/users" aria-current="page">
                  <FontAwesomeIcon className="fas" icon={faUsers} />
                  &nbsp;Users
                </Link>
              </li>
              <li class="is-active">
                <Link to={`/user`} aria-current="page">
                  <FontAwesomeIcon className="fas" icon={faEye} />
                  &nbsp;Detail (Sessions)
                </Link>
              </li>
            </ul>
          </nav>

          {/* Mobile Breadcrumbs */}
          <nav class="breadcrumb is-hidden-desktop" aria-label="breadcrumbs">
            <ul>
              <li class="">
                <Link to={`/users`} aria-current="page">
                  <FontAwesomeIcon className="fas" icon={faArrowLeft} />
                  &nbsp;Back to Users
                </Link>
              </li>
            </ul>
          </nav>

          {/* Content */}
          <nav class="box">
            <div
              class={`modal ${selectedSessionForDeletion ? "is-active" : ""}`}
            >
              <div class="modal-background"></div>
              <div class="modal-card">
                <header class="modal-card-head">
                  <p class="modal-card-title">Are you sure?</p>
                  <button
                    class="delete"
                    aria-label="close"
                    onClick={onDeselectSessionForDeletion}
                  ></button>
                </header>
                <section class="modal-card-body">
                  You are about to delete this session and all the data inside
                  of it. This action is cannot be undone. Are you sure you would
                  like to continue?
                </section>
                <footer class="modal-card-foot">
                  <button
                    class="button is-success"
                    onClick={onDeleteConfirmButtonClick}
                  >
                    Confirm
                  </button>
                  <button class="button" onClick={onDeselectSessionForDeletion}>
                    Cancel
                  </button>
                </footer>
              </div>
            </div>

            <div class="columns">
              <div class="column">
                <h1 class="title is-4">
                  <FontAwesomeIcon className="fas" icon={faUsers} />
                  &nbsp;User - {user.firstName} {user.lastName}
                </h1>
              </div>
              {!isFetching && (
                <div class="column has-text-right">
                  <button
                    class="button is-small is-info is-fullwidth-mobile"
                    onClick={defaultFetchList}
                  >
                    <FontAwesomeIcon className="mdi" icon={faRefresh} />
                    <span className="is-hidden-desktop is-hidden-tablet">
                      &nbsp;Refresh
                    </span>
                  </button>
                  &nbsp;
                  <button
                    onClick={(e, i) => {
                      setShowFilter(!showFilter);
                    }}
                    class="button is-small is-link is-fullwidth-mobile"
                    type="button"
                  >
                    <FontAwesomeIcon className="mdi" icon={faFilter} />
                    &nbsp;Filter
                  </button>
                  &nbsp;
                  <Link
                    to={`/user/${uid}/sessions/add`}
                    class="button is-small is-success is-fullwidth-mobile"
                    type="button"
                  >
                    <FontAwesomeIcon className="mdi" icon={faPlus} />
                    &nbsp;New Session
                  </Link>
                </div>
              )}
            </div>

            {/* Tab Navigation */}
            <div class="tabs is-medium">
              <ul>
                <li>
                  <Link class="is-active" to={`/user/${uid}`}>
                    Detail
                  </Link>
                </li>
                <li>
                  <Link>
                    <strong>Sessions</strong>
                  </Link>
                </li>
                <li>
                  <Link to={`/user/${uid}/activities`}>
                    Activities
                  </Link>
                </li>
                <li>
                  <Link
                    onClick={(userID) => {
                      onReportClick(uid);
                    }}
                  >
                    Report&nbsp;
                    <FontAwesomeIcon
                      className="mdi"
                      icon={faArrowUpRightFromSquare}
                    />
                  </Link>
                </li>
              </ul>
            </div>

            {/* Filter Box */}
            {showFilter && (
              <div
                class="has-background-white-bis"
                style={{ borderRadius: "15px", padding: "20px" }}
              >
                {/* Filter Title + Clear Button */}
                <div class="columns">
                  <div class="column is-half">
                    <strong>
                      <u>
                        <FontAwesomeIcon className="mdi" icon={faFilter} />
                        &nbsp;Filter
                      </u>
                    </strong>
                  </div>
                  <div class="column is-half has-text-right">
                    <Link onClick={onClearFilterClick}>
                      <FontAwesomeIcon
                        className="mdi"
                        icon={faFilterCircleXmark}
                      />
                      &nbsp;Clear Filter
                    </Link>
                  </div>
                </div>

                {/* Filter Options */}
                <div class="columns">
                  <div class="column">
                    <div class="">
                      <FormSelectField
                        label="Session Type"
                        name="Session Type"
                        placeholder="Text input"
                        selectedValue={sessionType}
                        options={
                          SESSION_TEMPLATE_SESSION_TYPE_WITH_EMPTY_OPTIONS
                        }
                        errorText={errors && errors.sessionType}
                        helpText=""
                        onChange={(e) => setSessionType(e.target.value)}
                        maxWidth="150px"
                      />
                    </div>
                  </div>
                  <div class="column">
                    <FormSelectField
                      label="Sort by"
                      name="sortByValue"
                      placeholder="Pick sorting"
                      selectedValue={sortByValue}
                      helpText=""
                      onChange={(e) => setSortByValue(e.target.value)}
                      options={SESSION_TEMPLATE_SORT_OPTIONS}
                      isRequired={true}
                    />
                  </div>
                </div>
              </div>
            )}

            {isFetching ? (
              <>
                <PageLoadingContent displayMessage={"Please wait"} />
              </>
            ) : (
              <>
                <FormErrorBox errors={errors} />
                {listData &&
                (listData.length > 0 || previousCursors.length > 0) ? (
                  <div class="container">
                    {/*
                                            ##################################################################
                                            EVERYTHING INSIDE HERE WILL ONLY BE DISPLAYED ON A DESKTOP SCREEN.
                                            ##################################################################
                                        */}
                    <div class="is-hidden-touch">
                      <UserSessionTemplateListDesktop
                        listData={listData}
                        setPageSize={setPageSize}
                        pageSize={pageSize}
                        previousCursors={previousCursors}
                        onPreviousClicked={onPreviousClicked}
                        onNextClicked={onNextClicked}
                      />
                    </div>

                    {/*
                                            ###########################################################################
                                            EVERYTHING INSIDE HERE WILL ONLY BE DISPLAYED ON A TABLET OR MOBILE SCREEN.
                                            ###########################################################################
                                        */}
                    <div class="is-fullwidth is-hidden-desktop">
                      <UserSessionTemplateListMobile
                        listData={listData}
                        setPageSize={setPageSize}
                        pageSize={pageSize}
                        previousCursors={previousCursors}
                        onPreviousClicked={onPreviousClicked}
                        onNextClicked={onNextClicked}
                      />
                    </div>
                  </div>
                ) : (
                  <div class="hero-body">
                    <p class="title">
                      <FontAwesomeIcon className="fas" icon={faTable} />
                      &nbsp;No data
                    </p>
                    <p class="subtitle">
                      No data.{" "}
                      <b>
                        <Link to={`/user/${uid}/sessions/add`}>
                          Click here&nbsp;
                          <FontAwesomeIcon
                            className="mdi"
                            icon={faArrowRight}
                          />
                        </Link>
                      </b>{" "}
                      to get started uploading your first video.
                    </p>
                  </div>
                )}
              </>
            )}

            <div class="columns pt-5">
              <div class="column is-half">
                <Link to="/users" class="button is-fullwidth-mobile">
                  <FontAwesomeIcon className="fas" icon={faArrowLeft} />
                  &nbsp;Back to Users
                </Link>
              </div>
              <div class="column is-half has-text-right">
                <Link
                  to={`/user/${uid}/sessions/add`}
                  class="button is-success is-fullwidth-mobile"
                >
                  <FontAwesomeIcon className="fas" icon={faPencil} />
                  &nbsp;New Session
                </Link>
              </div>
            </div>
          </nav>
        </section>
      </div>
    </>
  );
}

export default UserDetailForSessionList;
